/**
 * 패널 번호 — 재단·보관·적재·설치에서 같은 번호로 찾음
 * - 벽(하리 안쪽 포함): W01… 흐름 순서(변 순서 → 변 안에서 흐름 방향)
 * - 칸막이: P{칸막이 순번}-01…  /  천장: C01…  /  바닥: F01… (위·왼쪽부터)
 */
import { PolyPanel } from '../engine/polyEngine';

const pad = (n: number) => String(n).padStart(2, '0');

/** 벽·칸막이 패널 id → 번호 */
export function numberPanels(panels: PolyPanel[]): Record<string, string> {
  const out: Record<string, string> = {};
  // 흐름 방향으로 얼마나 갔나 (같은 변 안 정렬용)
  const along = (p: PolyPanel) => {
    const dx = p.b.x - p.a.x, dy = p.b.y - p.a.y;
    return Math.abs(dx) > Math.abs(dy) ? Math.sign(dx) * p.a.x : Math.sign(dy) * p.a.y;
  };
  const walls = panels.filter(p => !p.pieceKey.startsWith('part:')).sort((p, q) => p.edge - q.edge || along(p) - along(q));
  walls.forEach((p, i) => { out[p.id] = `W${pad(i + 1)}`; });
  // 칸막이: 칸막이별(나온 순서)로 묶고 그 안에서 왼→오 / 위→아래
  const parts = new Map<string, PolyPanel[]>();
  panels.filter(p => p.pieceKey.startsWith('part:')).forEach(p => {
    const k = p.pieceKey.split('#')[0];
    parts.set(k, [...(parts.get(k) ?? []), p]);
  });
  Array.from(parts.values()).forEach((ps, k) => {
    ps.sort((p, q) => Math.min(p.a.x, p.b.x) - Math.min(q.a.x, q.b.x) || Math.min(p.a.y, p.b.y) - Math.min(q.a.y, q.b.y))
      .forEach((p, i) => { out[p.id] = `P${k + 1}-${pad(i + 1)}`; });
  });
  return out;
}

export const ceilingNo = (i: number) => `C${pad(i + 1)}`;
export const floorNo = (i: number) => `F${pad(i + 1)}`;

/** 번호 목록을 짧게: W01, W02, W03, W07 → W01~W03, W07 */
export function compressNos(nos: string[]): string {
  const parse = (s: string) => { const m = s.match(/^(.*?)(\d+)$/); return m ? { pre: m[1], n: Number(m[2]), w: m[2].length } : null; };
  const sorted = [...nos].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const out: string[] = [];
  let i = 0;
  while (i < sorted.length) {
    const s = parse(sorted[i]);
    let j = i;
    while (s && j + 1 < sorted.length) {
      const t = parse(sorted[j + 1]);
      if (!t || t.pre !== s.pre || t.n !== s.n + (j + 1 - i)) break;
      j++;
    }
    out.push(j > i + 1 ? `${sorted[i]}~${sorted[j]}` : j === i + 1 ? `${sorted[i]}, ${sorted[j]}` : sorted[i]);
    i = j + 1;
  }
  return out.join(', ');
}
